import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  UserCheck,
  Heart,
  Mail,
  Link as LinkIcon,
  Settings,
  Star,
  Sparkles,
  Home,
  Search,
  Lock,
  BadgeCheck,
  Target,
  HandHeart,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { profileStatusUtils } from "../../services/queryClient";
import { useAuth } from "../../context/AuthContext";
import { toggleFollow } from "../../services/followService";
import { toggleAdmire, removeAdmire } from "../../services/admireService";
import AdmireModal from "../Profile/AdmireModal";
import ContactModal from "../Profile/ContactModal";
import StatsModal from "../Profile/StatsModal";

const ResponsiveProfileImage = ({ imageUrls, alt, className }) => {
  const getImageUrl = (size) => {
    if (!imageUrls) return null;
    if (typeof imageUrls === "string") return imageUrls;
    return imageUrls[size] || imageUrls.medium || imageUrls.large;
  };

  const mediumUrl = getImageUrl("medium");
  const largeUrl = getImageUrl("large");

  return (
    <picture>
      <source
        media="(max-width: 1024px)"
        type="image/avif"
        srcSet={`${mediumUrl || largeUrl} 1x, ${largeUrl} 2x`}
      />
      <source
        media="(min-width: 1025px)"
        type="image/avif"
        srcSet={`${largeUrl} 1x`}
      />
      <img
        src={mediumUrl || largeUrl}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};

const ProfileLeftSidebar = ({ userData }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { apiCall } = useAuth();

  const [isFollowing, setIsFollowing] = useState(
    userData?.followStatus?.isFollowing || false,
  );
  const [isFollowAccepted, setIsFollowAccepted] = useState(
    userData?.followStatus?.isAccepted || 0,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showAdmireModal, setShowAdmireModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isAdmiring, setIsAdmiring] = useState(
    userData?.admireStatus?.isAdmiring || false,
  );
  const [currentAdmireType, setCurrentAdmireType] = useState(
    userData?.admireStatus?.admireType || null,
  );
  const [isAccepted, setIsAccepted] = useState(
    userData?.admireStatus?.isAccepted || null,
  );
  const [isAdmireLoading, setIsAdmireLoading] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsModalTab, setStatsModalTab] = useState("followers");

  const admireOptions = [
    {
      icon: Star,
      label: "Inspiration",
      desc: "Their words spark ideas and push your creativity forward",
      value: "inspiration",
    },
    {
      icon: Sparkles,
      label: "Idol",
      desc: "A writer you deeply admire and aspire to become like",
      value: "idol",
    },
    {
      icon: Heart,
      label: "Adore",
      desc: "Someone whose writing you genuinely love and cherish",
      value: "adore",
    },
    {
      icon: HandHeart,
      label: "Support",
      desc: "A creator you believe in and want to encourage",
      value: "support",
    },
  ];

  const shouldShowLock = (statType) => {
    if (isOwnProfile) return false;

    const privacySettings = userData?.privacySettings || {};

    switch (statType) {
      case "followers":
        return privacySettings.showFollowers !== 1;
      case "following":
        return privacySettings.showFollowing !== 1;
      case "admirers":
        return privacySettings.showAdmirers !== 1;
      default:
        return false;
    }
  };

  const data = userData;
  const isOwnProfile = userData?.isOwnProfile;

  useEffect(() => {
    if (
      userData?.followStatus !== undefined &&
      userData?.followStatus !== null
    ) {
      setIsFollowing(userData.followStatus.isFollowing);
      setIsFollowAccepted(userData.followStatus.isAccepted || 0);
    }

    if (
      userData?.admireStatus !== undefined &&
      userData?.admireStatus !== null
    ) {
      setIsAdmiring(userData.admireStatus.isAdmiring);
      setCurrentAdmireType(userData.admireStatus.admireType);
      setIsAccepted(userData.admireStatus.isAccepted);
    }
  }, [userData?.followStatus, userData?.admireStatus]);

  useEffect(() => {
    if (showAdmireModal || showContactModal || showStatsModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAdmireModal, showContactModal, showStatsModal]);

  const handleFollowClick = async () => {
    setIsLoading(true);
    try {
      const result = await toggleFollow(apiCall, data.profileID);

      setIsFollowing(result.data.isFollowing);
      setIsFollowAccepted(result.data.isAccepted || 0);

      if (!result.data.isFollowing) {
        setIsAdmiring(false);
        setCurrentAdmireType(null);
        setIsAccepted(null);
                
        profileStatusUtils.updateAdmireStatus(
          data.profileID,
          false,
          null,
          Math.max((data.admirers || 0) - 1, 0)
        );
      }

      // if (queryClient) {
      //   // Update by username cache (Profile page)
      //   if (userData?.username) {
      //     queryClient.setQueryData(
      //       ["profile", "username", userData.username],
      //       (oldData) => {
      //         if (!oldData) return oldData;
      //         return {
      //           ...oldData,
      //           followers: result.data.newFollowerCount,
      //           followStatus: {
      //             isFollowing: result.data.isFollowing,
      //             isAccepted: result.data.isAccepted || 0,
      //           },
      //         };
      //       },
      //     );
      //   }

      //   // Update by postId cache (PostDetailPage)
      //   // Match ANY postId cache that contains this user's profileID
      //   queryClient.setQueriesData(
      //     { queryKey: ["profile", "postId"] },
      //     (oldData) => {
      //       if (!oldData || oldData.profileID !== data.profileID) {
      //         return oldData;
      //       }
      //       return {
      //         ...oldData,
      //         followers: result.data.newFollowerCount,
      //         followStatus: {
      //           isFollowing: result.data.isFollowing,
      //           isAccepted: result.data.isAccepted || 0,
      //         },
      //       };
      //     },
      //   );
      // }
      profileStatusUtils.updateFollowStatus(
        data.profileID,
        result.data.isFollowing,
        result.data.isAccepted,
        result.data.newFollowerCount
      );
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  const handleAdmireClick = async (option) => {
    if (isAdmiring && currentAdmireType !== option.value) {
      setShowAdmireModal(false);
      return;
    }

    setShowAdmireModal(false);
    setIsAdmireLoading(true);

    try {
      if (isAdmiring && currentAdmireType === option.value) {
        const result = await removeAdmire(apiCall, data.profileID);
        setIsAdmiring(false);
        setCurrentAdmireType(null);
        setIsAccepted(null);

        // if (queryClient) {
        //   // Update by username
        //   if (userData?.username) {
        //     queryClient.setQueryData(
        //       ["profile", "username", userData.username],
        //       (oldData) => {
        //         if (!oldData) return oldData;
        //         return {
        //           ...oldData,
        //           admirers: Math.max((oldData.admirers || 0) - 1, 0),
        //         };
        //       },
        //     );
        //   }

        //   // Update by postId
        //   queryClient.setQueriesData(
        //     { queryKey: ["profile", "postId"] },
        //     (oldData) => {
        //       if (!oldData || oldData.profileID !== data.profileID) {
        //         return oldData;
        //       }
        //       return {
        //         ...oldData,
        //         admirers: Math.max((oldData.admirers || 0) - 1, 0),
        //       };
        //     },
        //   );
        // }
        profileStatusUtils.updateAdmireStatus(
          data.profileID,
          false,
          null,
          Math.max((admirers || 0) - 1, 0)
        );
      } else {
        const result = await toggleAdmire(
          apiCall,
          data.profileID,
          option.value,
        );
        setIsAdmiring(result.data.isAdmiring);
        setCurrentAdmireType(result.data.admireType);
        setIsAccepted(result.data.isAccepted);

        // if (queryClient) {
        //   // Update by username
        //   if (userData?.username) {
        //     queryClient.setQueryData(
        //       ["profile", "username", userData.username],
        //       (oldData) => {
        //         if (!oldData) return oldData;
        //         return {
        //           ...oldData,
        //           admirers: (oldData.admirers || 0) + 1,
        //         };
        //       },
        //     );
        //   }

        //   // Update by postId
        //   queryClient.setQueriesData(
        //     { queryKey: ["profile", "postId"] },
        //     (oldData) => {
        //       if (!oldData || oldData.profileID !== data.profileID) {
        //         return oldData;
        //       }
        //       return {
        //         ...oldData,
        //         admirers: (oldData.admirers || 0) + 1,
        //       };
        //     },
        //   );
        // }
        profileStatusUtils.updateAdmireStatus(
          data.profileID,
          result.data.isAdmiring,
          result.data.admireType,
          (admirers || 0) + 1
        );
      }
    } catch (error) {
    } finally {
      setIsAdmireLoading(false);
    }
  };

  const handleStatsClick = (tab) => {
    if (isOwnProfile || userData?.privacySettings?.isPrivateAccount === 0) {
      setStatsModalTab(tab);
      setShowStatsModal(true);
      return;
    }

    if (isFollowing && isFollowAccepted === 1) {
      setStatsModalTab(tab);
      setShowStatsModal(true);
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const getAdmireButtonLabel = () => {
    if (!isAdmiring) return "Admire";

    return (
      currentAdmireType?.charAt(0).toUpperCase() + currentAdmireType?.slice(1)
    );
  };

  // Navigation handlers
  const handleCreatePost = () => {
    navigate("/create-post");
  };

  const handleGoHome = () => {
    navigate("/home");
  };

  const handleExplore = () => {
    navigate("/explore");
  };

  const handleForYou = () => {
    navigate("/feed?type=for_you");
  };

  const handleEditProfile = () => {
    navigate("/edit-profile");
  };

  const handleSettings = () => {
    navigate("/settings");
  };

  const {
    name,
    username: profileUsername,
    avatar,
    followers = 0,
    following = 0,
    admirers = 0,
    totalPosts = 0,
    bio,
    website,
    verified,
    privacySettings,
  } = userData;

  return (
    <>
      <aside className="hidden lg:block w-[335px] h-[calc(100vh-7rem)] sticky top-24">
        <div className="h-full flex flex-col bg-white hover:shadow-md transition-all duration-300 rounded-xl border border-gray-100 hover:shadow-md">
          <div className="flex-1 overflow-y-auto scroll-smooth scrollbar-hide">
            <div className="pt-6 px-6 pb-0 text-center">
              <div className="relative w-28 h-28 mx-auto mb-3">
                <div
                  className="absolute inset-0 rounded-full p-[3px]"
                  style={{
                    background:
                      "conic-gradient(from 0deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5, #feda75)",
                  }}
                >
                  <div className="w-full h-full rounded-full bg-white p-[3px] overflow-hidden">
                    {avatar ? (
                      <ResponsiveProfileImage
                        imageUrls={avatar}
                        alt={name}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                        {name ? (
                          <span className="text-slate-600 font-semibold text-4xl">
                            {getInitials(name)}
                          </span>
                        ) : (
                          <svg
                            className="w-full h-full text-slate-400"
                            viewBox="0 0 100 100"
                            fill="currentColor"
                          >
                            <circle cx="50" cy="35" r="18" />
                            <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-1 mb-1">
                <h2 className="text-lg sm:text-xl font-semibold text-black truncate">
                  {name}
                </h2>
              </div>

              <div className="flex items-center justify-center gap-1 mb-3">
                <p className="text-xs sm:text-sm font-normal text-black truncate">
                  {profileUsername}
                </p>

                {verified && (
                  <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white flex-shrink-0" />
                )}
              </div>

              <div className="flex flex-col gap-2 mb-5">
                {isOwnProfile ? (
                  <div className="flex gap-1 justify-center">
                    <button
                      onClick={handleEditProfile}
                      className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 text-white py-1.5 px-3 rounded-md font-medium flex items-center justify-center gap-1 text-xs"
                    >
                      Edit Profile
                    </button>
                    <button
                      onClick={handleSettings}
                      className="text-black py-1.5 px-3 rounded-md font-medium hover:bg-gray-50 transition-all duration-200 flex items-center justify-center text-xs"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    {!isFollowing ? (
                      <button
                        onClick={handleFollowClick}
                        disabled={isLoading}
                        className={`
                          w-full px-3 py-1 rounded-md text-sm font-semibold
                          transition-all duration-200
                          flex items-center justify-center gap-1.5 flex-shrink-0
                          bg-[#b0d284] text-black hover:bg-[#9ec170]
                          ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                        `}
                      >
                        {isLoading ? (
                          <div className="w-3.5 h-3.5 border-2 border-black/60 border-t-transparent rounded-full animate-spin" />
                        ) : isFollowing ? (
                          <>
                            <UserCheck className="w-3.5 h-3.5" />
                            <span>
                              {isFollowAccepted === 1
                                ? "Following"
                                : "Requested"}
                            </span>
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-3.5 h-3.5" />
                            <span>Follow</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="flex gap-2 justify-center">
                        {/* Follow Button */}
                        <button
                          onClick={handleFollowClick}
                          disabled={isLoading}
                          className={`
                            py-1.5 px-3 rounded-md font-medium text-xs shadow-md
                            transition-all duration-300 flex items-center justify-center gap-1
                            ${
                              isFollowing
                                ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg"
                            }
                            ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                          `}
                        >
                          {isLoading ? (
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isFollowing ? (
                            <>
                              <UserCheck className="w-3 h-3" />
                              <span>
                                {isFollowAccepted === 1
                                  ? "Following"
                                  : "Requested"}
                              </span>
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-3 h-3" />
                              <span>Follow</span>
                            </>
                          )}
                        </button>

                        {isFollowing && isFollowAccepted === 1 && (
                          <button
                            onClick={() => setShowAdmireModal(true)}
                            disabled={isAdmireLoading}
                            className={`
                              py-1.5 px-3 rounded-md font-medium text-xs shadow-md 
                              transition-all duration-300 flex items-center justify-center gap-1
                              ${
                                isAdmiring
                                  ? "bg-gradient-to-r from-pink-500 to-red-500 text-white hover:from-pink-600 hover:to-red-600"
                                  : "bg-gradient-to-r from-pink-100 to-red-100 text-pink-700 hover:from-pink-200 hover:to-red-200"
                              }
                              ${
                                isAdmireLoading
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }
                            `}
                          >
                            {isAdmireLoading ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Heart
                                  className={`w-3 h-3 ${
                                    isAdmiring ? "fill-current" : ""
                                  }`}
                                />
                                <span>{getAdmireButtonLabel()}</span>
                              </>
                            )}
                          </button>
                        )}

                        {isFollowing &&
                          isFollowAccepted === 1 &&
                          privacySettings?.showContactButton === 1 && (
                            <button
                              onClick={() => setShowContactModal(true)}
                              className="text-black py-1.5 px-3 rounded-md font-medium hover:bg-gray-50 transition-all duration-200 flex items-center justify-center text-xs"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex justify-center gap-6">
                <div className="flex flex-col items-center leading-tight cursor-pointer group">
                  <span className="text-lg font-semibold text-black">
                    {shouldShowLock("posts") ? (
                      <Lock className="w-5 h-5 mx-auto text-gray-400" />
                    ) : (
                      totalPosts
                    )}
                  </span>
                  <span className="text-[0.75rem] text-black leading-none mt-0.5">
                    Posts
                  </span>
                </div>

                <div
                  onClick={() => handleStatsClick("followers")}
                  className="flex flex-col items-center leading-tight cursor-pointer group"
                >
                  <span className="text-lg font-semibold text-black">
                    {shouldShowLock("followers") ? (
                      <Lock className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    ) : (
                      followers
                    )}
                  </span>
                  <span className="text-[0.75rem] text-black leading-none mt-0.5">
                    Followers
                  </span>
                </div>

                <div
                  onClick={() => handleStatsClick("following")}
                  className="flex flex-col items-center leading-tight cursor-pointer group"
                >
                  <span className="text-lg font-semibold text-black">
                    {shouldShowLock("following") ? (
                      <Lock className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    ) : (
                      following
                    )}
                  </span>
                  <span className="text-[0.75rem] text-black leading-none mt-0.5">
                    Following
                  </span>
                </div>

                <div
                  onClick={() => handleStatsClick("admirers")}
                  className="flex flex-col items-center leading-tight cursor-pointer group"
                >
                  <span className="text-lg font-semibold text-black">
                    {shouldShowLock("admirers") ? (
                      <Lock className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    ) : (
                      admirers
                    )}
                  </span>
                  <span className="text-[0.75rem] text-black leading-none mt-0.5">
                    Admirers
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 py-6 pt-3">
              {/* Bio */}
              {bio && (
                <div className="mb-1 w-full overflow-hidden"> 
                  <p className="text-sm font-normal text-black leading-relaxed break-words whitespace-pre-wrap text-left">
                    {bio}
                  </p>
                </div>
              )}

              {/* Details */}
              <div className="space-y-1">
                {website && (
                  <div className="flex items-center justify-center sm:justify-start gap-1 text-xs sm:text-sm text-black">
                    <LinkIcon className="w-4 h-4 flex-shrink-0" />
                    <a
                      href={
                        website.startsWith("http")
                          ? website
                          : `https://${website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      {website.replace(/^https?:\/\//, "")}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 bg-white mt-auto">
            <div className="grid grid-cols-4 gap-2">
              <div className="flex flex-col items-center">
                <button
                  onClick={handleCreatePost}
                  className="
                    w-8 h-8
                    flex items-center justify-center
                    rounded-md font-semibold
                    bg-[#b0d284] text-black
                    hover:bg-[#9ec170]
                    transition-all duration-200
                    active:scale-95
                  "
                >
                  <svg
                    className="block w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
                <span className="text-xs font-medium mt-2 text-black">
                  Create Post
                </span>
              </div>

              <div className="flex flex-col items-center">
                <button
                  onClick={handleGoHome}
                  className="w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 active:scale-95 hover:opacity-70"
                >
                  <Home className="w-5 h-5 stroke-black stroke-[2.5]" />
                </button>
                <span className="text-xs font-medium mt-2 text-black">
                  Home
                </span>
              </div>

              {/* Explore */}
              <div className="flex flex-col items-center">
                <button
                  onClick={handleExplore}
                  className="w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 active:scale-95 hover:opacity-70"
                >
                  <Search className="w-5 h-5 stroke-black stroke-[2.5]" />
                </button>
                <span className="text-xs font-medium mt-2 text-black">
                  Explore
                </span>
              </div>

              {/* For You */}
              <div className="flex flex-col items-center">
                <button
                  onClick={handleForYou}
                  className="w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 active:scale-95 hover:opacity-70"
                >
                  <Target className="w-5 h-5 stroke-black stroke-[2.5]" />
                </button>
                <span className="text-xs font-medium mt-2 text-black">
                  For You
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <AdmireModal
        showAdmireModal={showAdmireModal}
        setShowAdmireModal={setShowAdmireModal}
        admireOptions={admireOptions}
        handleAdmireClick={handleAdmireClick}
        isAdmiring={isAdmiring}
        currentAdmireType={currentAdmireType}
        userName={profileUsername}
      />

      <ContactModal
        showContactModal={showContactModal}
        setShowContactModal={setShowContactModal}
        userData={userData}
      />

      <StatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        initialTab={statsModalTab}
        profileData={userData}
      />
    </>
  );
};

export default ProfileLeftSidebar;